"use client";

import { StripePaymentForm } from "@/components/payment/StripePaymentForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Elements } from "@stripe/react-stripe-js";
import { type Stripe as StripeType, loadStripe } from "@stripe/stripe-js";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

if (!process.env.NEXT_PUBLIC_STRIPE_KEY) {
  throw new Error("NEXT_PUBLIC_STRIPE_KEY is not set");
}

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_KEY);

type StripeProduct = {
  id: string;
  name: string;
  description: string | null;
  prices: Array<{
    id: string;
    amount: number;
    currency: string;
    interval: string | null;
    intervalCount: number | null;
  }>;
};

export type AgentProfileData = {
  // Page 1 - Required
  accountHandle: string;
  telegramUsername: string;
  topics: string[];
  emojiPreference: "none" | "1-2" | "lots";

  // Page 2 - Optional
  website?: string;
  brandEmojis?: string[];
  referenceLinks?: Array<{ url: string; description: string }>; // Static content to learn from
  externalSources?: Array<{ url: string; description: string }>; // Dynamic fetcher sources
  safeguardsTopics?: string[];
  safeguardsWords?: string[];

  // Page 3 - Payment
  selectedPriceId?: string;
  referralCode?: string;

  // Stripe data
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;

  // Auth
  email: string;
  password: string;
};

// Internal types with unique IDs for React keys
type TopicWithId = { id: string; value: string };
type ReferenceLinkWithId = { id: string; url: string; description: string };
type ExternalSourceWithId = { id: string; url: string; description: string };

const generateId = () => Math.random().toString(36).substring(2, 11);

type Props = {
  onSubmit: (data: AgentProfileData) => Promise<void>;
  isSubmitting?: boolean;
};

export function AgentSignupForm({ onSubmit, isSubmitting = false }: Props) {
  const [currentStep, setCurrentStep] = useState(1);
  const [showSafeguards, setShowSafeguards] = useState(false);
  const [referralCodeValid, setReferralCodeValid] = useState<boolean | null>(null);
  const [discountInfo, setDiscountInfo] = useState<{
    percentOff: number | null;
    amountOff: number | null;
  } | null>(null);
  const [applyingCode, setApplyingCode] = useState(false);
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
  const [paymentStep, setPaymentStep] = useState<"selection" | "payment">("selection");

  const [formData, setFormData] = useState<Partial<AgentProfileData>>({
    emojiPreference: "none",
    brandEmojis: [],
    safeguardsTopics: [],
    safeguardsWords: [],
  });

  // Separate state for topics with unique IDs (for React keys)
  const [topics, setTopics] = useState<TopicWithId[]>([{ id: generateId(), value: "" }]);

  // Separate state for links with unique IDs (for React keys)
  const [referenceLinks, setReferenceLinks] = useState<ReferenceLinkWithId[]>([
    { id: generateId(), url: "", description: "" },
  ]);
  const [externalSources, setExternalSources] = useState<ExternalSourceWithId[]>([
    { id: generateId(), url: "", description: "" },
  ]);

  // Availability check states
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [accountHandleError, setAccountHandleError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Ref for scrolling to email input on error
  const emailInputRef = useRef<HTMLInputElement>(null);

  const updateField = <K extends keyof AgentProfileData>(field: K, value: AgentProfileData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Fetch products when component mounts
  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const response = await fetch("/api/stripe/products");
        const data = await response.json();
        setProducts(data.products || []);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, []);

  const updateTopic = (id: string, value: string) => {
    setTopics((prev) => prev.map((topic) => (topic.id === id ? { ...topic, value } : topic)));
  };

  const removeTopic = (id: string) => {
    setTopics((prev) => prev.filter((topic) => topic.id !== id));
  };

  const addTopic = () => {
    setTopics((prev) => [...prev, { id: generateId(), value: "" }]);
  };

  const addReferenceLink = () => {
    setReferenceLinks((prev) => [...prev, { id: generateId(), url: "", description: "" }]);
  };

  const updateReferenceLink = (id: string, field: "url" | "description", value: string) => {
    setReferenceLinks((prev) => prev.map((link) => (link.id === id ? { ...link, [field]: value } : link)));
  };

  const removeReferenceLink = (id: string) => {
    setReferenceLinks((prev) => prev.filter((link) => link.id !== id));
  };

  // External Sources (Fetchers) helpers
  const addExternalSource = () => {
    setExternalSources((prev) => [...prev, { id: generateId(), url: "", description: "" }]);
  };

  const updateExternalSource = (id: string, field: "url" | "description", value: string) => {
    setExternalSources((prev) => prev.map((source) => (source.id === id ? { ...source, [field]: value } : source)));
  };

  const removeExternalSource = (id: string) => {
    setExternalSources((prev) => prev.filter((source) => source.id !== id));
  };

  const canAddAnotherExternalSource = () => {
    if (externalSources.length === 0) return true;
    const lastSource = externalSources[externalSources.length - 1];
    return isValidUrl(lastSource.url);
  };

  const isValidUrl = (url: string) => {
    if (!url.trim()) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const getPasswordStrength = (password: string): { score: number; label: string; color: string } => {
    if (!password) return { score: 0, label: "", color: "" };

    let score = 0;

    // Length checks
    if (password.length >= 6) score += 1;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;

    // Character variety checks
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^a-zA-Z0-9]/.test(password)) score += 1;

    // Return strength level
    if (score <= 2) return { score: 1, label: "Weak", color: "bg-red-500" };
    if (score <= 4) return { score: 2, label: "Fair", color: "bg-yellow-500" };
    if (score <= 5) return { score: 3, label: "Good", color: "bg-blue-500" };
    return { score: 4, label: "Strong", color: "bg-green-500" };
  };

  const passwordStrength = getPasswordStrength(formData.password || "");

  const canAddAnotherLink = () => {
    if (referenceLinks.length === 0) return true;
    const lastLink = referenceLinks[referenceLinks.length - 1];
    return isValidUrl(lastLink.url);
  };

  const canProceedFromStep1 = () => {
    const filledTopics = topics.filter((t) => t.value.trim().length > 0);
    return (
      formData.accountHandle?.trim() &&
      formData.telegramUsername?.trim() &&
      filledTopics.length >= 1 &&
      formData.emojiPreference
    );
  };

  const canProceedFromStep3 = () => {
    return formData.email?.trim() && formData.password && formData.password.length >= 6 && formData.selectedPriceId;
  };

  const checkAccountHandleAvailability = async (): Promise<boolean> => {
    if (!formData.accountHandle?.trim()) return false;

    setCheckingAvailability(true);
    setAccountHandleError(null);

    try {
      const response = await fetch("/api/auth/check-availability", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accountHandle: formData.accountHandle.trim() }),
      });

      const data = await response.json();

      if (!data.accountHandleAvailable) {
        setAccountHandleError("This account handle is already taken. Please choose another.");
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error checking account handle:", error);
      setAccountHandleError("Failed to check availability. Please try again.");
      return false;
    } finally {
      setCheckingAvailability(false);
    }
  };

  const checkEmailAvailability = async (): Promise<boolean> => {
    if (!formData.email?.trim()) return false;

    try {
      const response = await fetch("/api/auth/check-availability", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email.trim() }),
      });

      const data = await response.json();

      if (!data.emailAvailable) {
        setEmailError("This email is already registered. Please use a different email or sign in.");
        // Scroll to email input so user can see the error
        emailInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        emailInputRef.current?.focus();
        return false;
      }

      setEmailError(null);
      return true;
    } catch (error) {
      console.error("Error checking email:", error);
      setEmailError("Failed to check email availability. Please try again.");
      emailInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      emailInputRef.current?.focus();
      return false;
    }
  };

  const handleContinueToStep2 = async () => {
    if (!canProceedFromStep1()) return;

    const isAvailable = await checkAccountHandleAvailability();
    if (isAvailable) {
      setCurrentStep(2);
    }
  };

  const applyPromotionCode = async () => {
    const code = formData.referralCode?.trim().toUpperCase();
    if (!code) {
      setReferralCodeValid(null);
      setDiscountInfo(null);
      return;
    }

    setApplyingCode(true);
    try {
      const response = await fetch("/api/stripe/validate-promotion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ promotionCode: code }),
      });

      if (!response.ok) {
        setReferralCodeValid(false);
        setDiscountInfo(null);
        setApplyingCode(false);
        return;
      }

      const data = await response.json();
      setReferralCodeValid(true);
      setDiscountInfo({
        percentOff: data.discount.percentOff,
        amountOff: data.discount.amountOff,
      });
    } catch (error) {
      console.error("Error validating promotion code:", error);
      setReferralCodeValid(false);
      setDiscountInfo(null);
    } finally {
      setApplyingCode(false);
    }
  };

  const calculateDiscountedPrice = (originalPrice: number) => {
    if (!discountInfo) return originalPrice;

    if (discountInfo.percentOff) {
      return originalPrice * (1 - discountInfo.percentOff / 100);
    }

    if (discountInfo.amountOff) {
      return Math.max(0, originalPrice - discountInfo.amountOff / 100);
    }

    return originalPrice;
  };

  const validateReferralCode = async (code: string) => {
    const trimmedCode = code.trim().toUpperCase();
    if (!trimmedCode) {
      setReferralCodeValid(null);
      return;
    }

    try {
      // We'll validate the coupon when creating the setup intent
      setReferralCodeValid(null);
    } catch (error) {
      setReferralCodeValid(false);
    }
  };

  const handleProceedToPayment = async () => {
    if (!canProceedFromStep3()) return;

    // Check email availability first
    const emailAvailable = await checkEmailAvailability();
    if (!emailAvailable) return;

    try {
      // Create setup intent
      const response = await fetch("/api/stripe/create-setup-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          priceId: formData.selectedPriceId,
          email: formData.email,
          couponCode: formData.referralCode?.trim().toUpperCase(),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        setReferralCodeValid(false);
        alert(error.error || "Failed to initialize payment");
        return;
      }

      const data = await response.json();
      setClientSecret(data.clientSecret);
      setCustomerId(data.customerId);
      setSetupIntentId(data.setupIntentId);
      setReferralCodeValid(formData.referralCode ? true : null);
      setPaymentStep("payment");
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to initialize payment");
    }
  };

  const handlePaymentSuccess = async (paymentMethodId: string) => {
    if (!customerId || !formData.selectedPriceId) return;

    try {
      // Create subscription with the payment method
      const response = await fetch("/api/stripe/create-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerId,
          priceId: formData.selectedPriceId,
          paymentMethodId,
          couponCode: formData.referralCode?.trim().toUpperCase(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Subscription creation failed:", errorData);
        throw new Error(errorData.error || "Failed to create subscription");
      }

      const data = await response.json();

      // Add Stripe data and links to form before submitting
      // Strip the internal 'id' field from links/sources/topics before saving
      const finalData: AgentProfileData = {
        ...formData,
        topics: topics.map(({ value }) => value).filter((v) => v.trim()),
        referenceLinks: referenceLinks.map(({ url, description }) => ({ url, description })),
        externalSources: externalSources.map(({ url, description }) => ({ url, description })),
        stripeCustomerId: customerId,
        stripeSubscriptionId: data.subscriptionId,
      } as AgentProfileData;

      await onSubmit(finalData);
    } catch (error) {
      console.error("Error creating subscription:", error);
      alert("Failed to create subscription");
    }
  };

  const handlePaymentError = (error: string) => {
    alert(error);
  };

  const getSelectedPrice = () => {
    if (!formData.selectedPriceId) return null;
    for (const product of products) {
      const price = product.prices.find((p) => p.id === formData.selectedPriceId);
      if (price) return price;
    }
    return null;
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Progress Indicator */}
      <div className="flex items-center justify-center mb-12">
        <div className="flex items-center gap-4">
          {/* Step 1 */}
          <div className="flex flex-col items-center">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold text-lg transition-colors ${
                currentStep === 1
                  ? "bg-[#43BEA9] text-white"
                  : currentStep > 1
                    ? "bg-[#43BEA9] text-white"
                    : "bg-gray-700 text-gray-400"
              }`}
            >
              {currentStep > 1 ? "✓" : "1"}
            </div>
            <span className="text-xs mt-2 text-gray-400">Profile Basics</span>
          </div>

          {/* Connector */}
          <div className={`w-24 h-0.5 ${currentStep > 1 ? "bg-[#43BEA9]" : "bg-gray-700"}`} />

          {/* Step 2 */}
          <div className="flex flex-col items-center">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold text-lg transition-colors ${
                currentStep === 2
                  ? "bg-[#43BEA9] text-white"
                  : currentStep > 2
                    ? "bg-[#43BEA9] text-white"
                    : "bg-gray-700 text-gray-400"
              }`}
            >
              {currentStep > 2 ? "✓" : "2"}
            </div>
            <span className="text-xs mt-2 text-gray-400">Refinement</span>
          </div>

          {/* Connector */}
          <div className={`w-24 h-0.5 ${currentStep > 2 ? "bg-[#43BEA9]" : "bg-gray-700"}`} />

          {/* Step 3 */}
          <div className="flex flex-col items-center">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold text-lg transition-colors ${
                currentStep === 3 ? "bg-[#43BEA9] text-white" : "bg-gray-700 text-gray-400"
              }`}
            >
              3
            </div>
            <span className="text-xs mt-2 text-gray-400">Subscription</span>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-gray-900 rounded-lg p-8 border border-gray-800">
        {/* Step 1: Agent Basics */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Agent Basics</h2>
              <p className="text-gray-400">Set up the core identity for your agent.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="accountHandle" className="text-white">
                  Account Handle <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                  <Input
                    id="accountHandle"
                    value={formData.accountHandle || ""}
                    onChange={(e) => {
                      updateField("accountHandle", e.target.value.replace(/\s/g, ""));
                      setAccountHandleError(null); // Clear error when user types
                    }}
                    placeholder="my_agent_handle"
                    className="pl-8 bg-gray-800 border-gray-700 text-white"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Pick a name for your handle.</p>
              </div>

              <div>
                <Label htmlFor="telegramUsername" className="text-white">
                  Telegram Username <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="telegramUsername"
                  value={formData.telegramUsername || ""}
                  onChange={(e) => updateField("telegramUsername", e.target.value.replace(/\s/g, ""))}
                  placeholder="telegram_user"
                  className="bg-gray-800 border-gray-700 text-white mt-1"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Used for onboarding instructions.</p>
              </div>
            </div>

            <div>
              <Label className="text-white mb-2 block">
                Content Buckets <span className="text-red-500">*</span>
              </Label>
              <p className="text-sm text-gray-400 mb-4">What you want your agent to post about</p>
              <div className="space-y-3">
                {topics.map((topic) => (
                  <div key={topic.id} className="flex gap-2">
                    <Textarea
                      value={topic.value}
                      onChange={(e) => updateTopic(topic.id, e.target.value)}
                      placeholder="Topic | Angle or opinion"
                      className="bg-gray-800 border-gray-700 text-white min-h-[60px] resize-none flex-1"
                    />
                    {topics.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTopic(topic.id)}
                        className="text-gray-400 hover:text-white self-start pt-4"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  onClick={addTopic}
                  variant="outline"
                  className="w-full border-gray-700 bg-gray-800 text-white hover:bg-gray-700 hover:text-white"
                >
                  + Add another bucket
                </Button>
              </div>
              <div className="mt-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Examples</p>
                <ul className="text-sm text-gray-400 space-y-1">
                  <li>Crypto | Where the industry is heading long term</li>
                  <li>Finance | How people can manage money more efficiently</li>
                  <li>Technology | How new tools are changing everyday work</li>
                  <li>Lifestyle | Habits that improve focus and balance</li>
                  <li>Travel | How to get more out of each destination</li>
                </ul>
              </div>
            </div>

            <div>
              <Label htmlFor="emojiPreference" className="text-white">
                Emoji Preference
              </Label>
              <Select
                id="emojiPreference"
                value={formData.emojiPreference || "none"}
                onChange={(e) => updateField("emojiPreference", e.target.value as "none" | "1-2" | "lots")}
                className="bg-gray-800 border-gray-700 text-white mt-1"
              >
                <option value="none">None</option>
                <option value="1-2">1-2 max</option>
                <option value="lots">Lots</option>
              </Select>
            </div>

            {accountHandleError && (
              <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
                {accountHandleError}
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button
                onClick={handleContinueToStep2}
                disabled={!canProceedFromStep1() || checkingAvailability}
                className="bg-[#43BEA9] hover:bg-[#3AAD98] text-white font-semibold px-8"
              >
                {checkingAvailability ? "Checking..." : "Continue to Refinement"}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Fine-tune Tone (Optional) */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Fine-tune Tone</h2>
              <div className="inline-block bg-[#43BEA9]/20 text-[#43BEA9] text-xs px-2 py-1 rounded mb-2">OPTIONAL</div>
              <p className="text-gray-400">Settings to help the agent capture your unique voice.</p>
            </div>

            <div>
              <Label htmlFor="website" className="text-white">
                Website
              </Label>
              <Input
                id="website"
                type="url"
                value={formData.website || ""}
                onChange={(e) => updateField("website", e.target.value)}
                placeholder="your website url"
                className="bg-gray-800 border-gray-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">References to Learn From</Label>
              <p className="text-sm text-gray-400 mb-3">
                Share blogs, podcasts, or product pages for the agent to learn your voice and style.
              </p>

              {referenceLinks.map((link) => (
                <div key={link.id} className="mb-3 p-4 bg-gray-800 rounded-lg border border-gray-700 relative">
                  <button
                    onClick={() => removeReferenceLink(link.id)}
                    className="absolute top-2 right-2 text-gray-400 hover:text-white"
                    type="button"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="space-y-2 pr-8">
                    <div>
                      <Label className="text-white text-xs">URL</Label>
                      <Input
                        value={link.url}
                        onChange={(e) => updateReferenceLink(link.id, "url", e.target.value)}
                        placeholder="https://yourblog.com/article"
                        className="bg-gray-900 border-gray-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white text-xs">DESCRIPTION</Label>
                      <Input
                        value={link.description}
                        onChange={(e) => updateReferenceLink(link.id, "description", e.target.value)}
                        placeholder="e.g. My writing style examples, product info"
                        className="bg-gray-900 border-gray-700 text-white mt-1"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <Button
                onClick={addReferenceLink}
                variant="outline"
                disabled={!canAddAnotherLink()}
                className="w-full border-gray-700 bg-gray-800 text-white hover:bg-gray-700 hover:text-white mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
              >
                + Add another reference
              </Button>
            </div>

            <div>
              <Label className="text-white mb-2 block">External Sources (Fetchers)</Label>
              <p className="text-sm text-gray-400 mb-3">
                Add real-time data sources for the agent to pull from (e.g., price feeds, news).
              </p>
              <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30 mb-4 flex items-start gap-2">
                <svg
                  className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                  role="img"
                >
                  <title>Info</title>
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-blue-300">These may take a few business days to appear on your profile</p>
              </div>

              {externalSources.map((source) => (
                <div key={source.id} className="mb-3 p-4 bg-gray-800 rounded-lg border border-gray-700 relative">
                  <button
                    onClick={() => removeExternalSource(source.id)}
                    className="absolute top-2 right-2 text-gray-400 hover:text-white"
                    type="button"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="space-y-2 pr-8">
                    <div>
                      <Label className="text-white text-xs">URL</Label>
                      <Input
                        value={source.url}
                        onChange={(e) => updateExternalSource(source.id, "url", e.target.value)}
                        placeholder="https://api.coingecko.com/..."
                        className="bg-gray-900 border-gray-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-white text-xs">TRIGGER CONDITION</Label>
                      <Input
                        value={source.description}
                        onChange={(e) => updateExternalSource(source.id, "description", e.target.value)}
                        placeholder="e.g. Post when $ETH changes ±5%"
                        className="bg-gray-900 border-gray-700 text-white mt-1"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <Button
                onClick={addExternalSource}
                variant="outline"
                disabled={!canAddAnotherExternalSource()}
                className="w-full border-gray-700 bg-gray-800 text-white hover:bg-gray-700 hover:text-white mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
              >
                + Add another source
              </Button>
            </div>

            <div>
              <button
                onClick={() => setShowSafeguards(!showSafeguards)}
                className="flex items-center gap-2 text-white cursor-pointer mb-2 hover:text-[#43BEA9] transition-colors"
                type="button"
              >
                {showSafeguards ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span>Advanced: Safeguards (Topics/Words to Avoid)</span>
              </button>
              {showSafeguards && (
                <div className="space-y-3 pl-4">
                  <div>
                    <Label className="text-white text-sm">Topics to avoid</Label>
                    <div className="mt-1 p-2 bg-gray-800 border border-gray-700 rounded-md min-h-[60px] flex flex-wrap gap-2 items-start">
                      {(formData.safeguardsTopics || [])
                        .filter((t) => t.trim())
                        .map((topic) => (
                          <span
                            key={`safeguard-topic-${topic}`}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-gray-700 text-white text-sm rounded"
                          >
                            {topic}
                            <button
                              type="button"
                              onClick={() => {
                                const newTopics = (formData.safeguardsTopics || []).filter((t) => t !== topic);
                                updateField("safeguardsTopics", newTopics);
                              }}
                              className="text-gray-400 hover:text-white"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      <input
                        type="text"
                        placeholder={formData.safeguardsTopics?.length ? "" : "Type and press Enter or comma"}
                        className="flex-1 min-w-[150px] bg-transparent border-none outline-none text-white text-sm placeholder:text-gray-500"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === ",") {
                            e.preventDefault();
                            const value = e.currentTarget.value.trim();
                            if (value) {
                              updateField("safeguardsTopics", [...(formData.safeguardsTopics || []), value]);
                              e.currentTarget.value = "";
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white text-sm">Words to avoid</Label>
                    <div className="mt-1 p-2 bg-gray-800 border border-gray-700 rounded-md min-h-[60px] flex flex-wrap gap-2 items-start">
                      {(formData.safeguardsWords || [])
                        .filter((w) => w.trim())
                        .map((word) => (
                          <span
                            key={`safeguard-word-${word}`}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-gray-700 text-white text-sm rounded"
                          >
                            {word}
                            <button
                              type="button"
                              onClick={() => {
                                const newWords = (formData.safeguardsWords || []).filter((w) => w !== word);
                                updateField("safeguardsWords", newWords);
                              }}
                              className="text-gray-400 hover:text-white"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      <input
                        type="text"
                        placeholder={formData.safeguardsWords?.length ? "" : "Type and press Enter or comma"}
                        className="flex-1 min-w-[150px] bg-transparent border-none outline-none text-white text-sm placeholder:text-gray-500"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === ",") {
                            e.preventDefault();
                            const value = e.currentTarget.value.trim();
                            if (value) {
                              updateField("safeguardsWords", [...(formData.safeguardsWords || []), value]);
                              e.currentTarget.value = "";
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={() => setCurrentStep(1)}
                variant="ghost"
                className="text-white hover:bg-gray-800 hover:text-white"
              >
                Back
              </Button>
              <div className="flex gap-3">
                <Button
                  onClick={() => setCurrentStep(3)}
                  variant="ghost"
                  className="text-gray-400 hover:bg-gray-800 hover:text-white"
                >
                  Skip for now
                </Button>
                <Button
                  onClick={() => setCurrentStep(3)}
                  className="bg-[#43BEA9] hover:bg-[#3AAD98] text-white font-semibold px-8"
                >
                  Next: Payment
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Payment Setup */}
        {currentStep === 3 && (
          <div className="space-y-6">
            {paymentStep === "selection" ? (
              <>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Choose your plan</h2>
                  <p className="text-gray-400">All plans include a 14-day free trial. Cancel anytime.</p>
                </div>

                {/* Auth Fields */}
                <div className="space-y-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
                  <h3 className="text-white font-semibold">Account Setup</h3>
                  <div>
                    <Label htmlFor="email" className="text-white">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      ref={emailInputRef}
                      value={formData.email || ""}
                      onChange={(e) => {
                        updateField("email", e.target.value);
                        setEmailError(null); // Clear error when user types
                      }}
                      placeholder="you@example.com"
                      className="bg-gray-900 border-gray-700 text-white mt-1"
                      required
                    />
                    {emailError && <p className="text-red-400 text-sm mt-1">{emailError}</p>}
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-white">
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password || ""}
                      onChange={(e) => updateField("password", e.target.value)}
                      placeholder="At least 6 characters"
                      className="bg-gray-900 border-gray-700 text-white mt-1"
                      minLength={6}
                      required
                    />
                    {formData.password && (
                      <div className="mt-2">
                        <div className="flex gap-1 mb-1">
                          {[1, 2, 3, 4].map((level) => (
                            <div
                              key={level}
                              className={`h-1 flex-1 rounded-full transition-colors ${
                                level <= passwordStrength.score ? passwordStrength.color : "bg-gray-700"
                              }`}
                            />
                          ))}
                        </div>
                        <p
                          className={`text-xs ${
                            passwordStrength.score <= 1
                              ? "text-red-400"
                              : passwordStrength.score <= 2
                                ? "text-yellow-400"
                                : passwordStrength.score <= 3
                                  ? "text-blue-400"
                                  : "text-green-400"
                          }`}
                        >
                          {passwordStrength.label}
                          {passwordStrength.score < 3 && (
                            <span className="text-gray-500 ml-1">
                              — Try adding {!/[A-Z]/.test(formData.password) && "uppercase, "}
                              {!/[0-9]/.test(formData.password) && "numbers, "}
                              {!/[^a-zA-Z0-9]/.test(formData.password) && "symbols"}
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Plan Selection from Stripe */}
                {loadingProducts ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">Loading plans...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {products.map((product) =>
                      product.prices.map((price) => (
                        <button
                          key={price.id}
                          onClick={() => updateField("selectedPriceId", price.id)}
                          className={`w-full p-6 rounded-lg border-2 transition-all text-left ${
                            formData.selectedPriceId === price.id
                              ? "border-[#43BEA9] bg-gray-800"
                              : "border-gray-700 bg-gray-800/50 hover:border-gray-600"
                          }`}
                          type="button"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <span className="text-white font-semibold block">{product.name}</span>
                              {product.description && (
                                <span className="text-sm text-gray-400">{product.description}</span>
                              )}
                            </div>
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                formData.selectedPriceId === price.id
                                  ? "border-[#43BEA9] bg-[#43BEA9]"
                                  : "border-gray-600"
                              }`}
                            >
                              {formData.selectedPriceId === price.id && (
                                <div className="w-2 h-2 bg-gray-900 rounded-full" />
                              )}
                            </div>
                          </div>
                          <div className="text-3xl font-bold text-white mb-2">
                            {discountInfo ? (
                              <>
                                <span className="line-through text-gray-500 text-2xl mr-2">${price.amount}</span>
                                <span className="text-[#43BEA9]">
                                  ${calculateDiscountedPrice(price.amount).toFixed(2)}
                                </span>
                              </>
                            ) : (
                              <>${price.amount}</>
                            )}
                            {price.interval && (
                              <span className="text-lg text-gray-400">
                                /{price.interval === "month" ? "mo" : price.interval}
                              </span>
                            )}
                          </div>
                        </button>
                      )),
                    )}
                  </div>
                )}

                {/* Referral Code */}
                <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
                  <Label htmlFor="referralCode" className="text-white mb-2 block">
                    Referral Code (Optional)
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="referralCode"
                      value={formData.referralCode || ""}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        updateField("referralCode", value);
                        // Reset validation when user types
                        if (referralCodeValid !== null) {
                          setReferralCodeValid(null);
                          setDiscountInfo(null);
                        }
                      }}
                      placeholder="ENTER CODE"
                      className="bg-gray-900 border-gray-700 text-white uppercase"
                      disabled={false}
                    />
                    <Button
                      type="button"
                      onClick={applyPromotionCode}
                      disabled={!formData.referralCode?.trim() || applyingCode}
                      className="bg-[#43BEA9] hover:bg-[#3AAD98] text-white font-semibold px-6"
                    >
                      {applyingCode ? "Checking..." : "Apply"}
                    </Button>
                  </div>
                  {referralCodeValid === true && discountInfo && (
                    <p className="text-[#43BEA9] text-sm mt-2">
                      ✓{" "}
                      {discountInfo.percentOff
                        ? `${discountInfo.percentOff}% off`
                        : `$${((discountInfo.amountOff ?? 0) / 100).toFixed(2)} off`}{" "}
                      applied!
                    </p>
                  )}
                  {referralCodeValid === false && <p className="text-red-400 text-sm mt-2">Invalid promotion code</p>}
                </div>

                {/* Info Box */}
                <div className="p-6 bg-gray-800 rounded-lg border border-gray-700">
                  <div className="flex justify-between text-white text-xl font-bold mb-2">
                    <span>Total Today</span>
                    <span>$0.00</span>
                  </div>
                  <p className="text-[#43BEA9] text-sm">14-Day Free Trial Active</p>
                  {getSelectedPrice() && (
                    <>
                      <p className="text-gray-400 text-xs mt-2">Billing starts after 14 days.</p>
                      {discountInfo && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="flex justify-between text-sm text-gray-400 mb-1">
                            <span>Original price:</span>
                            <span>${getSelectedPrice()?.amount}</span>
                          </div>
                          <div className="flex justify-between text-sm text-[#43BEA9] mb-1">
                            <span>Discount:</span>
                            <span>
                              {discountInfo.percentOff
                                ? `-${discountInfo.percentOff}%`
                                : `-$${((discountInfo.amountOff ?? 0) / 100).toFixed(2)}`}
                            </span>
                          </div>
                          <div className="flex justify-between text-lg text-white font-semibold">
                            <span>After trial:</span>
                            <span>${calculateDiscountedPrice(getSelectedPrice()?.amount ?? 0).toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                      {!discountInfo && (
                        <p className="text-gray-400 text-sm mt-2">First charge: ${getSelectedPrice()?.amount}</p>
                      )}
                    </>
                  )}
                </div>

                <div className="flex justify-between pt-4">
                  <Button
                    onClick={() => setCurrentStep(2)}
                    variant="ghost"
                    className="text-white hover:bg-gray-800 hover:text-white"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleProceedToPayment}
                    disabled={!canProceedFromStep3()}
                    className="bg-[#43BEA9] hover:bg-[#3AAD98] text-white font-semibold px-8"
                  >
                    Continue to Payment →
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Add Payment Method</h2>
                  <p className="text-gray-400">Secure your 14-day free trial. You won't be charged today.</p>
                  {referralCodeValid && <p className="text-[#43BEA9] text-sm mt-2">✓ Discount code applied!</p>}
                </div>

                {clientSecret && (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret,
                      appearance: {
                        theme: "night",
                        variables: {
                          colorPrimary: "#43BEA9",
                          colorBackground: "#1f2937",
                          colorText: "#ffffff",
                          colorDanger: "#ef4444",
                        },
                      },
                    }}
                  >
                    <StripePaymentForm
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                      isSubmitting={isSubmitting}
                    />
                  </Elements>
                )}

                <div className="flex justify-start pt-4">
                  <Button
                    onClick={() => setPaymentStep("selection")}
                    variant="ghost"
                    className="text-white hover:bg-gray-800 hover:text-white"
                    disabled={isSubmitting}
                  >
                    ← Back to Plan Selection
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
